import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Modal } from '../../Components';
import { motion } from 'framer-motion';
import { accountService, alertService } from '../../Services';

export default function ModalPricingSignIn(props) {
    const initForm = {
        email: '',
        password: '',
    };
    const navigate = useNavigate();
    const [form, setForm] = useState(initForm);
    const [searchParams] = useSearchParams();
    const [showErrorMessage, setShowErrorMessage] = useState("");
    const [error, setError] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const [loading, setLoading] = useState(false);

    const setInput = key => event => updateForm({ [key]: event.target.value });
    const updateForm = data => setForm({ ...form, ...data });

    useEffect(() => {
        const link = document.createElement("link");
        link.rel = "stylesheet";
        link.href = "https://cdnjs.cloudflare.com/ajax/libs/font-awesome/6.5.0/css/all.min.css";
        link.crossOrigin = "anonymous";
        link.referrerPolicy = "no-referrer";
        document.head.appendChild(link);

        return () => {
            document.head.removeChild(link);
        };
    }, []);

    const handleSignUpClick = () => {
        props.setModal('pricingformCreate');
    };

    const handleLoginWithGoogle = () => {
        window.location.href = `${process.env.REACT_APP_API_URL}/login/google/action/signin/role/3/0/referalPath/pricing/1`;
    };

    const loginValidation = () => {
        if (form.email === '' || form.password === '') {
            setError(true);
            return;
        }
        
        setLoading(true);
        alertService.clear();
        accountService
            .login(form['email'], form['password'], props.subsProductId)
            .then(res => {
                if (res['paypalurl'] && res['paypalurl'] !== "") {
                    window.location.replace(res['paypalurl']);
                }
                if (res['data'] && res['data']['roles']) {
                    if (
                        res['data']['role'] &&
                        res['data']['role'] === 'advertiser' &&
                        res['data']['active_role'] === 3
                    ) {
                        navigate('/brand/influencerdiscovery');
                    } else {
                        navigate('/account/login');
                    }
                } else {
                    navigate('/account/login');
                }
            })
            .catch(err => {
                setShowErrorMessage(err);
            })
            .finally(() => setLoading(false));
    };

    // Handle Enter key for login
    useEffect(() => {
        const keyDownHandler = (e) => e.key === "Enter" && loginValidation();
        document.addEventListener("keydown", keyDownHandler);
        return () => document.removeEventListener("keydown", keyDownHandler);
    }, [form]);

    // Google Icon
    const GoogleIcon = () => (
        <svg className="w-5 h-5 sm:w-6 sm:h-6" viewBox="0 0 24 24">
            <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
            <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
            <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
            <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
        </svg>
    );

    // Logo component matching Login.jsx
    const Logo = () => (
        <div className="flex items-center gap-2 sm:gap-3">
            <div
                className="w-10 h-10 sm:w-12 sm:h-12 rounded-xl flex items-center justify-center shadow-lg"
                style={{
                    background: "linear-gradient(135deg, #10b981 0%, #059669 50%, #047857 100%)",
                    boxShadow: "0 4px 16px rgba(16, 185, 129, 0.3)",
                }}
            >
                <img
                    src={process.env.PUBLIC_URL + "/favicon/favicon.ico"}
                    className="h-5 w-5 sm:h-7 sm:w-7 drop-shadow-md"
                    alt="Logo"
                />
            </div>
            <span
                className="font-bold text-xl sm:text-2xl tracking-wide"
                style={{
                    background: "linear-gradient(90deg, #059669, #10b981, #34d399)",
                    WebkitBackgroundClip: "text",
                    WebkitTextFillColor: "transparent",
                }}
            >
                TAMA
            </span>
        </div>
    );

    return (
        <Modal addClass='platform pricingModal' close={props.close} className="right-4 top-1">
            <div className="w-full max-w-[420px] mx-auto px-4 sm:px-6 py-4 sm:py-6 bg-white rounded-2xl">
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.4 }}
                    className="w-full"
                >
                    {/* Logo */}
                    <div className="flex items-center justify-center mb-4 sm:mb-6">
                        <Logo />
                    </div>

                    {/* Heading */}
                    <h1 className="text-lg sm:text-xl lg:text-2xl font-semibold text-gray-900 mb-4 sm:mb-6 text-center">
                        Login
                    </h1>

                    {/* Google Sign In Button */}
                    <button 
                        onClick={handleLoginWithGoogle}
                        className="w-full py-2.5 sm:py-3 lg:py-3.5 text-xs sm:text-sm lg:text-base font-medium bg-white border border-gray-200 rounded-lg sm:rounded-xl flex items-center justify-center gap-2 sm:gap-3 transition-all duration-200 hover:bg-gray-50 hover:border-gray-300 mb-4 sm:mb-6"
                    >
                        <GoogleIcon />
                        Sign in with Google
                    </button>

                    {/* Divider */}
                    <div className="flex items-center gap-3 mb-4 sm:mb-6">
                        <div className="flex-1 h-px bg-gray-200"></div>
                        <span className="text-xs sm:text-sm text-gray-400 font-medium">or</span>
                        <div className="flex-1 h-px bg-gray-200"></div>
                    </div>

                    {/* Email Input */}
                    <div className="mb-3 sm:mb-4">
                        <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1 sm:mb-1.5">E-mail</label>
                        <input
                            type="email"
                            placeholder="Your email address"
                            value={form.email}
                            onChange={setInput("email")}
                            className={`w-full px-3 sm:px-4 py-2.5 sm:py-3 text-sm sm:text-base border rounded-lg outline-none transition-all duration-200 bg-white focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100 ${
                                error && !form.email ? "border-red-500" : "border-gray-200"
                            }`}
                            autoFocus
                        />
                        {error && !form.email && (
                            <span className="text-red-500 text-xs mt-1 block">Please enter Email</span>
                        )}
                    </div>

                    {/* Password Input */}
                    <div className="mb-3 sm:mb-4">
                        <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1 sm:mb-1.5">Password</label>
                        <div className="relative">
                            <input
                                type={showPassword ? "text" : "password"}
                                placeholder="Your password"
                                value={form.password}
                                onChange={setInput("password")}
                                className={`w-full px-3 sm:px-4 py-2.5 sm:py-3 pr-10 sm:pr-12 text-sm sm:text-base border rounded-lg outline-none transition-all duration-200 bg-white focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100 ${
                                    error && !form.password ? "border-red-500" : "border-gray-200"
                                }`}
                            />
                            <i
                                className={`fas ${showPassword ? "fa-eye" : "fa-eye-slash"} absolute right-3 sm:right-4 top-1/2 -translate-y-1/2 cursor-pointer text-gray-400 hover:text-gray-600 text-sm sm:text-base`}
                                onClick={() => setShowPassword(!showPassword)}
                            ></i>
                        </div>
                        {error && !form.password && (
                            <span className="text-red-500 text-xs mt-1 block">Please enter Password</span>
                        )}
                    </div>

                    {/* Remember Me & Forgot Password Row */}
                    <div className="flex  xs:flex-row items-start xs:items-center justify-between gap-2 mb-5 sm:mb-6">
                        <label className="flex items-center gap-2 cursor-pointer">
                            <input 
                                type="checkbox" 
                                className="w-4 h-4 rounded border-gray-300 text-indigo-500 focus:ring-indigo-500"
                            />
                            <span className="text-xs sm:text-sm text-gray-600">Remember me</span>
                        </label>
                        <Link to="/account/forgot-pass" className="text-xs sm:text-sm text-indigo-500 hover:text-indigo-600 transition-colors font-medium">
                            Forgot Password?
                        </Link>
                    </div>

                    {/* Error Message */}
                    {showErrorMessage && (
                        <div className="bg-red-50 text-red-600 px-3 py-2 rounded-lg mb-3 sm:mb-4 text-xs sm:text-sm text-center">
                            {showErrorMessage}
                        </div>
                    )}

                    {/* Login Button */}
                    <button
                        onClick={loginValidation}
                        disabled={loading}
                        className={`w-full py-2.5 sm:py-3 text-sm sm:text-base font-semibold text-white rounded-full transition-all duration-200 mb-4 sm:mb-6 ${
                            loading
                                ? "bg-gray-400 cursor-not-allowed"
                                : "bg-gray-900 hover:bg-gray-800 active:scale-[0.98]"
                        }`}
                    >
                        {loading ? "LOGGING IN..." : "LOGIN"}
                    </button>

                    {/* Sign Up Link */}
                    <p className="text-center text-xs sm:text-sm text-gray-500">
                        Don't have an account?{" "}
                        <button
                            onClick={handleSignUpClick}
                            className="text-indigo-500 font-medium hover:text-indigo-600"
                        >
                            Sign Up
                        </button>
                    </p>
                </motion.div>
            </div>
        </Modal>
    );
}